import moment from "moment";
import React, { useEffect, useRef, Component } from "react";
import IconButton from "../../../IconButton";
import { FieldProps } from "../../Field";
import { FieldProps as FormikFieldProps } from "formik";
import { DateRangePicker as AirBnBDateRangePicker, FocusedInputShape, DateRangePickerShape } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";

export interface DateRangePickerProps extends Partial<FieldProps> {
	locale: string;
	wrapperId: string;
	field: FormikFieldProps["field"];
	form: FormikFieldProps["form"];
	name: string;
	endDateValueName: string;
	startDateValueName: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
	locale,
	wrapperId,
	field,
	form,
	name,
	startDateValueName,
	endDateValueName,
	id,
	label,
	className,
	innerRef,
	onFocus,
	type,
	onBlur,
	children,
	...props
}) => {
	moment.locale(locale);

	const [startDate, setStartDate] = React.useState<moment.Moment | null>(null);
	const [endDate, setEndDate] = React.useState<moment.Moment | null>(null);
	const [focusedInput, setFocusedInput] = React.useState<FocusedInputShape | null>(null);
	const [key, setKey] = React.useState<number>(0);
	const containerRef = useRef<Component<DateRangePickerShape, any, any>>(null);

	useEffect(() => {
		if (field.value[startDateValueName] !== startDate?.format("YYYY-MM-DD")) {
			const updatedDate = moment(field.value[startDateValueName]).isValid()
				? moment(field.value[startDateValueName])
				: null;
			setStartDate(updatedDate);
		}

		if (field.value[endDateValueName] !== endDate?.format("YYYY-MM-DD")) {
			const updatedDate = moment(field.value[endDateValueName]).isValid()
				? moment(field.value[endDateValueName])
				: null;
			setEndDate(updatedDate);
		}
	}, [field.value]);

	const clearDates = () => {
		//eslint-disable-next-line @typescript-eslint/ban-ts-comment
		//@ts-ignore
		//Manually clear input fields and trigger a re-render.
		const inputFields = containerRef.current?.container.querySelectorAll("input");
		inputFields.forEach((input: HTMLInputElement) => {
			input.value = "";
		});
		setStartDate(null);
		setEndDate(null);

		form.setFieldValue(`${field.name}[${startDateValueName}]`, null);
		form.setFieldValue(`${field.name}[${endDateValueName}]`, null);

		setKey(key + 1);
	};

	return (
		<div id={wrapperId} className="field">
			<AirBnBDateRangePicker
				numberOfMonths={1}
				key={key}
				ref={containerRef}
				startDate={startDate}
				startDateId="start-date"
				endDate={endDate}
				endDateId="end-date"
				onDatesChange={({ startDate, endDate }) => {
					setStartDate(startDate);
					setEndDate(endDate);
				}}
				focusedInput={focusedInput}
				onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
				onClose={(final) => {
					form.setFieldValue(
						`${field.name}[${startDateValueName}]`,
						final.startDate ? moment(final.startDate).format("YYYY-MM-DD") : null
					);

					form.setFieldValue(
						`${field.name}[${endDateValueName}]`,
						final.endDate ? moment(final.endDate).format("YYYY-MM-DD") : null
					);
				}}
				keepOpenOnDateSelect={false}
				hideKeyboardShortcutsPanel
				minimumNights={0}
				{...props}
			/>
			<IconButton classes={"flat clear-dates-btn"} icon="times" ariaLabel="Clear dates" onClick={clearDates} />
		</div>
	);
};

export default DateRangePicker;
